<script lang="ts" setup>
import { onMounted, ref } from 'vue'
import { onKeyStroke } from '@vueuse/core'

const props = defineProps({
  destroyed: {
    type: [Function, () => undefined],
    required: false,
    default: () => {},
  },
  innerClass: {
    type: String,
    required: false,
    default: '',
  },
  maxWidth: {
    type: String,
    required: false,
    default: 'sm:max-w-5xl',
  },
})

const active = ref(false)
onMounted(() => active.value = true)
onKeyStroke('Escape', (e: KeyboardEvent) => {
  e.preventDefault()
  destroy()
})

async function destroy() {
  active.value = false
  setTimeout(() => {
    if (props && props.destroyed)
      props.destroyed()
  }, 200)
}

defineExpose({ destroy })
</script>

<template>
  <div id="ModalBase" class="fixed inset-0 z-111 overflow-y-auto">
    <div class="min-h-screen flex items-end justify-center px-4 pb-20 pt-4 sm:block sm:p-0">
      <transition
        enter-active-class="ease-out duration-300"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="ease-in duration-200"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div v-if="active" class="fixed inset-0 transition-opacity">
          <div class="absolute inset-0 bg-black opacity-75" />
          <div class="fixed right-0 top-0 p-3">
            <div class="cursor-pointer rounded-full p-3 transition duration-150 ease-in-out hover:bg-gray-800" @click="destroy">
              <div aria-hidden="true" class="h-6 w-6 text-white" i-mdi-close @click="destroy" />
            </div>
          </div>
        </div>
      </transition>
      <span class="hidden sm:inline-block sm:h-full sm:align-middle">&nbsp;</span>
      <transition
        enter-active-class="ease-out duration-300"
        enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
        enter-to-class="opacity-100 translate-y-0 sm:scale-100"
        leave-active-class="ease-in duration-200"
        leave-from-class="opacity-100 translate-y-0 sm:scale-100"
        leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
      >
        <div
          v-if="active"
          class="fixed top-0 inline-block transform overflow-hidden border-1 rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow shadow-xl transition-all sm:my-16 sm:w-full dark:bg-gray-800 sm:p-6 dark:border-gray-700"
          :class="[innerClass, maxWidth]"
          role="dialog"
          aria-modal="true"
          aria-labelledby="modal-headline"
        >
          <slot />
        </div>
      </transition>
    </div>
  </div>
</template>
